/**
 * On-demand flow pattern survey: similarity scoring (time / size / premium) and
 * heuristic multi-leg labels. No automatic background runs.
 */

import type { ScreenerRow } from '../types/flowScreenerRow';
import type {
  FlowExecutionHint,
  FlowPatternClassification,
  FlowPatternMatchMode,
  FlowPatternPayoffEstimate,
  FlowPatternSurveyCandidate,
  FlowPatternSurveyOptions,
  FlowPatternRight,
  NormalizedFlowLeg,
} from '../types/flowPatternLeg';
import { getApiUrl } from '../config/api';
import { getAuthToken } from './authUtils';

/** Historical tape row (Flow Analyzer / contract modal). */
export interface HistoricalTradeLike {
  id?: number | string;
  time?: string;
  /** Lowercase alias some APIs use */
  ts?: string;
  Time?: string;
  timestamp?: string | number;
  opra_trade_timestamp?: string;
  premium?: number;
  volume?: number;
  open_interest?: number;
  side?: string;
  execution_side?: string;
  price?: number;
  /** YYYY-MM-DD when the API only returns clock time; used with `time` for survey windows. */
  sessionDayYmd?: string;
}

/** Live flow / pattern-candidates API row. */
export interface FlowRowLike {
  Time?: string;
  ts?: string;
  Ticker?: string;
  ticker?: string;
  Underlying?: string;
  root?: string;
  Strike?: number;
  strike?: number;
  'Expiry Date'?: string;
  expiry?: string;
  'Option Type'?: string;
  option_type?: string;
  Premium?: number;
  premium?: number;
  Volume?: number;
  volume?: number;
  Size?: number;
  size?: number;
  Side?: string;
  side?: string;
  Delta?: number;
  delta?: number;
  Contract?: string;
  contract?: string;
  nbbo_bid?: number;
  nbbo_ask?: number;
  nbbo_mid?: number;
  spread_at_print?: number;
  vs_mid?: number;
  'Open Interest'?: number;
  open_interest?: number;
  OI?: number;
  oi?: number;
}

function pickStr(...vals: unknown[]): string {
  for (const v of vals) {
    if (v === null || v === undefined) continue;
    const s = String(v).trim();
    if (s) return s;
  }
  return '';
}

function pickNum(...vals: unknown[]): number {
  for (const v of vals) {
    if (v === null || v === undefined || v === '') continue;
    const n = typeof v === 'number' ? v : parseFloat(String(v).replace(/,/g, ''));
    if (Number.isFinite(n)) return n;
  }
  return 0;
}

function normalizeExpiryYmd(exp: string): string {
  const s = pickStr(exp).replace(/\//g, '-');
  const digits = s.replace(/\D/g, '');
  if (digits.length >= 8) return `${digits.slice(0, 4)}-${digits.slice(4, 6)}-${digits.slice(6, 8)}`;
  if (/^\d{4}-\d{2}-\d{2}/.test(s)) return s.slice(0, 10);
  return s.slice(0, 10);
}

function parseRight(ot: string): FlowPatternRight {
  const u = ot.toUpperCase();
  if (u.startsWith('P') || u.includes('PUT')) return 'P';
  return 'C';
}

/** YYYY-MM-DD from sessionDateYmd helper. */
function ymdKey(sessionDateYmd: string): string {
  return sessionDateYmd.replace(/\//g, '-').trim().slice(0, 10);
}

/**
 * When `clockOrRaw` has no calendar day (e.g. "14:30:00" or "9:35:12 AM"), combine with session YYYY-MM-DD.
 * Returns null if session missing, string already has a date, or parse fails.
 */
function tryMsFromSessionClock(sessionDateYmd: string | undefined, clockOrRaw: string): number | null {
  if (!sessionDateYmd) return null;
  const ymd = ymdKey(sessionDateYmd);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(ymd)) return null;
  const s = String(clockOrRaw).trim();
  if (!s || /^\d{4}-\d{2}-\d{2}/.test(s)) return null;
  if (/[ap]m/i.test(s)) {
    const d = new Date(`${ymd} ${s}`);
    return Number.isNaN(d.getTime()) ? null : d.getTime();
  }
  if (/^\d{1,2}:\d{2}(:\d{2})?$/.test(s)) {
    const d = new Date(`${ymd}T${s}`);
    return Number.isNaN(d.getTime()) ? null : d.getTime();
  }
  return null;
}

/**
 * Parse timestamp to ms; returns NaN if unknown.
 * @param sessionDateYmd Optional YYYY-MM-DD for trades whose API returns clock-only `time` / `timestamp`.
 */
export function parseFlowTimestampMs(
  row: {
    ts?: string;
    Time?: string;
    time?: string;
    timestamp?: string | number;
    opra_trade_timestamp?: string;
  },
  sessionDateYmd?: string,
): number {
  const raw = row.opra_trade_timestamp ?? row.timestamp ?? row.ts;
  if (raw != null && raw !== '') {
    if (typeof raw === 'number') {
      let ms = raw;
      if (ms > 1e15) ms = Math.floor(ms / 1e6);
      else if (ms < 1e11) ms *= 1000;
      const d = new Date(ms);
      if (!Number.isNaN(d.getTime())) return d.getTime();
    } else {
      const s = String(raw).trim();
      const patched = tryMsFromSessionClock(sessionDateYmd, s);
      if (patched != null) return patched;
      const d = new Date(s.replace(' ', 'T'));
      if (!Number.isNaN(d.getTime())) return d.getTime();
    }
  }
  const ts = pickStr(row.ts);
  if (ts) {
    const patchedTs = tryMsFromSessionClock(sessionDateYmd, ts);
    if (patchedTs != null) return patchedTs;
    const d = new Date(ts.replace(' ', 'T'));
    if (!Number.isNaN(d.getTime())) return d.getTime();
  }
  const clock = pickStr(row.Time, row.time);
  if (clock) {
    const patchedClock = tryMsFromSessionClock(sessionDateYmd, clock);
    if (patchedClock != null) return patchedClock;
    const d = new Date(clock);
    if (!Number.isNaN(d.getTime())) return d.getTime();
  }
  return NaN;
}

function executionHint(sideRaw: string): FlowExecutionHint {
  const s = sideRaw.toLowerCase();
  // Align with tape/NBBO convention used elsewhere (e.g. ContractHistoricalModal): print at or above
  // ask ≈ buyer lifted the offer ("buy"); at or below bid ≈ seller hit the bid ("sell").
  if (s === 'buy' || s === 'b') return 'buy';
  if (s === 'sell' || s === 's') return 'sell';
  if (s === 'ask') return 'buy';
  if (s === 'bid') return 'sell';
  if (s === 'mid' || s === 'm') return 'mid';
  return 'unknown';
}

export function normalizeFlowRow(row: FlowRowLike, index: number): NormalizedFlowLeg | null {
  const underlying = pickStr(row.Ticker, row.ticker, row.Underlying, row.root).toUpperCase();
  if (!underlying) return null;
  const expiryYmd = normalizeExpiryYmd(pickStr(row['Expiry Date'], row.expiry));
  const strike = pickNum(row.Strike, row.strike);
  if (!Number.isFinite(strike) || strike <= 0) return null;
  const right = parseRight(pickStr(row['Option Type'], row.option_type, 'C'));
  const tsMs = parseFlowTimestampMs(row);
  if (!Number.isFinite(tsMs)) return null;
  const vol = Math.round(pickNum(row.Volume, row.volume, row.Size, row.size));
  const prem = pickNum(row.Premium, row.premium);
  const sideRaw = pickStr(row.Side, row.side);
  const legId = `${underlying}_${expiryYmd}_${strike}_${right}_${tsMs}_${index}`;
  const contract = pickStr(row.Contract, row.contract);
  const base: NormalizedFlowLeg = {
    legId,
    underlying,
    expiryYmd,
    strike,
    right,
    timestampMs: tsMs,
    volume: vol,
    premium: prem,
    executionHint: executionHint(sideRaw),
    delta: pickNum(row.Delta, row.delta) || undefined,
    sourceLabel: contract || `${underlying} ${expiryYmd} ${strike}${right}`,
  };
  const nb = (k: keyof FlowRowLike): number | undefined => {
    const v = row[k];
    if (v === null || v === undefined || v === '') return undefined;
    const n = typeof v === 'number' ? v : parseFloat(String(v));
    return Number.isFinite(n) ? n : undefined;
  };
  const bid = nb('nbbo_bid');
  const ask = nb('nbbo_ask');
  const mid = nb('nbbo_mid');
  const spr = nb('spread_at_print');
  const vm = nb('vs_mid');
  if (bid !== undefined) base.nbbo_bid = bid;
  if (ask !== undefined) base.nbbo_ask = ask;
  if (mid !== undefined) base.nbbo_mid = mid;
  if (spr !== undefined) base.spread_at_print = spr;
  if (vm !== undefined) base.vs_mid = vm;
  const oiRaw = pickNum(row['Open Interest'], row.open_interest, row.OI, row.oi);
  if (Number.isFinite(oiRaw) && oiRaw >= 0) base.openInterest = Math.round(oiRaw);
  return base;
}

export function normalizeHistoricalTradeRow(
  row: HistoricalTradeLike,
  ctx: {
    underlying: string;
    expiryYmd: string;
    strike: number;
    right: FlowPatternRight;
    /** Session calendar day (Flow Analyzer session date) when rows lack full ISO timestamps. */
    sessionDateYmd?: string;
  },
  index: number,
): NormalizedFlowLeg | null {
  const session =
    pickStr(row.sessionDayYmd) || pickStr(ctx.sessionDateYmd) || undefined;
  const tsMs = parseFlowTimestampMs(row, session);
  if (!Number.isFinite(tsMs)) return null;
  const vol = Math.round(pickNum(row.volume));
  const prem = pickNum(row.premium);
  const sideRaw = pickStr(row.side, row.execution_side);
  const legId = `ht_${ctx.underlying}_${ctx.expiryYmd}_${ctx.strike}_${ctx.right}_${row.id ?? index}_${tsMs}`;
  const base: NormalizedFlowLeg = {
    legId,
    underlying: ctx.underlying.toUpperCase(),
    expiryYmd: ctx.expiryYmd,
    strike: ctx.strike,
    right: ctx.right,
    timestampMs: tsMs,
    volume: vol,
    premium: prem,
    executionHint: executionHint(sideRaw),
    sourceLabel: `${ctx.underlying} ${ctx.expiryYmd} ${ctx.strike} ${ctx.right === 'C' ? 'Call' : 'Put'}`,
  };
  const oiRaw = pickNum(row.open_interest);
  if (Number.isFinite(oiRaw) && oiRaw >= 0) base.openInterest = Math.round(oiRaw);
  return base;
}

/** Calendar days from anchor session (local midnight) to expiry date (not trading DTE). */
export function calendarDaysToExpiry(anchorMs: number, expiryYmd: string): number {
  const exp = normalizeExpiryYmd(expiryYmd);
  const d = exp.replace(/\D/g, '');
  if (d.length < 8) return 9999;
  const y = parseInt(d.slice(0, 4), 10);
  const mo = parseInt(d.slice(4, 6), 10) - 1;
  const day = parseInt(d.slice(6, 8), 10);
  const a = new Date(anchorMs);
  const session0 = Date.UTC(a.getFullYear(), a.getMonth(), a.getDate());
  const exp0 = Date.UTC(y, mo, day);
  return Math.round((exp0 - session0) / 86400000);
}

/** Signed calendar days between two expiry dates (leg minus anchor). */
export function expiryCalendarDeltaDays(anchorExpiryYmd: string, legExpiryYmd: string): number {
  const a = normalizeExpiryYmd(anchorExpiryYmd).replace(/\D/g, '');
  const b = normalizeExpiryYmd(legExpiryYmd).replace(/\D/g, '');
  if (a.length < 8 || b.length < 8) return 0;
  const da = Date.UTC(+a.slice(0, 4), +a.slice(4, 6) - 1, +a.slice(6, 8)) / 86400000;
  const db = Date.UTC(+b.slice(0, 4), +b.slice(4, 6) - 1, +b.slice(6, 8)) / 86400000;
  return Math.round(db - da);
}

/** Distinct contracts from anchor + legs (any expiry; multi-expiry packages and tape pulls). */
export function uniqContractsFromPatternCandidates(
  anchor: NormalizedFlowLeg,
  candidates: NormalizedFlowLeg[],
  maxContracts: number,
): Array<{ underlying: string; expiryYmd: string; strike: number; right: FlowPatternRight }> {
  const cap = Math.max(1, Math.min(maxContracts, 25));
  const key = (l: NormalizedFlowLeg) =>
    `${l.underlying.toUpperCase()}|${l.expiryYmd}|${l.strike}|${l.right}`;
  const byKey = new Map<string, NormalizedFlowLeg>();
  const add = (l: NormalizedFlowLeg) => {
    if (l.underlying.toUpperCase() !== anchor.underlying.toUpperCase()) return;
    const k = key(l);
    if (!byKey.has(k)) byKey.set(k, l);
  };
  add(anchor);
  for (const c of candidates) add(c);
  const anchorKey = key(anchor);
  const legDesc = (l: NormalizedFlowLeg) => ({
    underlying: l.underlying.toUpperCase(),
    expiryYmd: l.expiryYmd,
    strike: l.strike,
    right: l.right,
  });
  const others = Array.from(byKey.entries())
    .filter(([k]) => k !== anchorKey)
    .map(([, leg]) => leg);
  /** Prefer farther expiries / strikes first so tape augmentation hits calendars & spreads, not only the anchor line. */
  others.sort((a, b) => {
    const da = Math.abs(expiryCalendarDeltaDays(anchor.expiryYmd, a.expiryYmd));
    const db = Math.abs(expiryCalendarDeltaDays(anchor.expiryYmd, b.expiryYmd));
    if (da !== db) return db - da;
    const sa = Math.abs(a.strike - anchor.strike);
    const sb = Math.abs(b.strike - anchor.strike);
    if (sa !== sb) return sb - sa;
    return a.timestampMs - b.timestampMs;
  });
  return [legDesc(anchor), ...others.slice(0, cap - 1).map(legDesc)];
}

/**
 * For each contract, GET same-day historical trades (one request per contract), then normalize to legs.
 * Sequential with a short delay to avoid hammering the API.
 */
export async function fetchTapeLegsSequentialForContracts(
  contracts: Array<{ underlying: string; expiryYmd: string; strike: number; right: FlowPatternRight }>,
  sessionDayYmd: string,
  opts?: { pageSize?: number; delayMs?: number },
): Promise<NormalizedFlowLeg[]> {
  const pageSize = Math.min(Math.max(opts?.pageSize ?? 8000, 100), 20000);
  const delayMs = Math.max(150, opts?.delayMs ?? 320);
  const base = getApiUrl('/api/smart-flow/historical-trades');
  const headers: Record<string, string> = {};
  const token = getAuthToken();
  if (token) headers.Authorization = `Bearer ${token}`;
  const session = normalizeExpiryYmd(sessionDayYmd);
  const legs: NormalizedFlowLeg[] = [];
  let globalIdx = 0;

  for (const c of contracts) {
    const sym = encodeURIComponent(c.underlying);
    const exp = encodeURIComponent(c.expiryYmd.replace(/\//g, '-').slice(0, 10));
    const stk = encodeURIComponent(String(c.strike));
    const ot = c.right === 'P' ? 'put' : 'call';
    const url = `${base}?symbol=${sym}&expiry=${exp}&strike=${stk}&optionType=${ot}&page=1&page_size=${pageSize}`;
    const res = await fetch(url, { headers });
    const data = (await res.json().catch(() => ({}))) as { trades?: unknown[] };
    const trades = Array.isArray(data?.trades) ? data.trades : [];
    trades.forEach((raw, i) => {
      const t = raw as Record<string, unknown>;
      const vol = Number(t.volume ?? t.quantity ?? 0) || 0;
      const price = Number(t.price ?? 0) || 0;
      const prem = Number(t.premium ?? (price * vol * 100)) || 0;
      let time: string | undefined = typeof t.time === 'string' ? t.time : undefined;
      const stampRaw = t.timestamp ?? t.opra_trade_timestamp;
      if (!time && stampRaw != null) {
        if (typeof stampRaw === 'number') {
          let ms = stampRaw;
          if (ms > 1e15) ms = Math.floor(ms / 1e6);
          else if (ms < 1e11) ms *= 1000;
          time = new Date(ms).toISOString().slice(11, 19);
        } else {
          time = String(stampRaw).slice(11, 19);
        }
      }
      const timestamp: string | number | undefined =
        typeof stampRaw === 'string' || typeof stampRaw === 'number' ? stampRaw : undefined;
      const oiTape =
        t.open_interest != null && t.open_interest !== ''
          ? Number(t.open_interest)
          : t.openInterest != null && t.openInterest !== ''
            ? Number(t.openInterest)
            : undefined;
      const row: HistoricalTradeLike = {
        id: (t.id as number | string | undefined) ?? i,
        time,
        timestamp,
        volume: vol,
        premium: prem,
        price,
        side: typeof t.side === 'string' ? t.side : undefined,
        execution_side: typeof t.execution_side === 'string' ? t.execution_side : undefined,
        sessionDayYmd: session,
        ...(oiTape != null && Number.isFinite(oiTape) && oiTape >= 0 ? { open_interest: oiTape } : {}),
      };
      const n = normalizeHistoricalTradeRow(
        row,
        {
          underlying: c.underlying,
          expiryYmd: c.expiryYmd,
          strike: c.strike,
          right: c.right,
          sessionDateYmd: session,
        },
        globalIdx++,
      );
      if (n) legs.push(n);
    });
    await new Promise((r) => setTimeout(r, delayMs));
  }
  return legs;
}

export function normalizeScreenerRow(row: ScreenerRow, index: number): NormalizedFlowLeg | null {
  const underlying = pickStr(row.ticker, row.snapshotSymbol).toUpperCase();
  if (!underlying) return null;
  const expiryYmd = normalizeExpiryYmd(pickStr(row.expiry));
  const strike = pickNum(row.strike);
  if (!Number.isFinite(strike) || strike <= 0) return null;
  const right = parseRight(pickStr(row.option_type, 'C'));
  const tsMs = row.last_time ? parseFlowTimestampMs({ ts: row.last_time }) : Date.now();
  if (!Number.isFinite(tsMs)) return null;
  const vol = Math.round(pickNum(row.volume, row.call_vol + row.put_vol));
  const prem = pickNum(row.premium);
  const sideRaw = pickStr(row.side);
  const legId = `scr_${underlying}_${expiryYmd}_${strike}_${right}_${index}`;
  const oiScr = row.oi != null ? pickNum(row.oi) : NaN;
  const base: NormalizedFlowLeg = {
    legId,
    underlying,
    expiryYmd,
    strike,
    right,
    timestampMs: tsMs,
    volume: vol,
    premium: prem,
    executionHint: executionHint(sideRaw),
    delta: row.delta != null ? pickNum(row.delta) : row.delta_sum != null ? pickNum(row.delta_sum) : undefined,
    sourceLabel: pickStr(row.contract) || `${underlying} ${expiryYmd} ${strike}${right}`,
  };
  if (Number.isFinite(oiScr) && oiScr >= 0) base.openInterest = Math.round(oiScr);
  return base;
}

function premiumClose(a: number, b: number, tolPct: number): boolean {
  if (a <= 0 && b <= 0) return true;
  const m = Math.max(Math.abs(a), Math.abs(b), 1);
  return Math.abs(a - b) / m <= tolPct / 100;
}

function volumeClose(a: number, b: number, tol: number): boolean {
  return Math.abs(a - b) <= tol;
}

const EXEC_SIDE_PENALTY = 14;

function hasExecutableSide(h: FlowExecutionHint): boolean {
  return h === 'buy' || h === 'sell';
}

/** Both legs have buy/sell and they differ. */
export function executionSideMismatch(anchor: NormalizedFlowLeg, leg: NormalizedFlowLeg): boolean {
  return hasExecutableSide(anchor.executionHint) && hasExecutableSide(leg.executionHint) && anchor.executionHint !== leg.executionHint;
}

export function sameContractIdentity(a: NormalizedFlowLeg, b: NormalizedFlowLeg): boolean {
  return (
    a.underlying.toUpperCase() === b.underlying.toUpperCase() &&
    a.expiryYmd === b.expiryYmd &&
    a.strike === b.strike &&
    a.right === b.right
  );
}

/**
 * Narrow pre-selection buckets before users scan the full ranked table.
 * Mutually exclusive by legId: same-clock co-prints first, then same-size, then other line (strike or expiry differs).
 * `samePrintMaxDeltaMs` uses wall-clock delta (independent of surveyTimeCluster's internal epsilon).
 */
export function buildFlowQuickPickBuckets(
  anchor: NormalizedFlowLeg,
  candidates: FlowPatternSurveyCandidate[],
  opts?: { volumeAbsTol?: number; maxPerBucket?: number; samePrintMaxDeltaMs?: number },
): {
  sameClock: FlowPatternSurveyCandidate[];
  sameVolume: FlowPatternSurveyCandidate[];
  otherLine: FlowPatternSurveyCandidate[];
} {
  const maxB = Math.max(1, Math.min(opts?.maxPerBucket ?? 8, 20));
  const volTol = Math.max(0, opts?.volumeAbsTol ?? Math.max(1, Math.round(Math.abs(anchor.volume) * 0.02)));
  const printEps = Math.max(0, opts?.samePrintMaxDeltaMs ?? 50);
  const used = new Set<string>();
  const push = (arr: FlowPatternSurveyCandidate[], c: FlowPatternSurveyCandidate) => {
    if (used.has(c.legId)) return;
    if (sameContractIdentity(anchor, c)) return;
    if (arr.length >= maxB) return;
    used.add(c.legId);
    arr.push(c);
  };
  const sameClock: FlowPatternSurveyCandidate[] = [];
  const sameVolume: FlowPatternSurveyCandidate[] = [];
  const otherLine: FlowPatternSurveyCandidate[] = [];
  for (const c of candidates) {
    if (Math.abs(c.timestampMs - anchor.timestampMs) <= printEps) push(sameClock, c);
  }
  for (const c of candidates) {
    if (Math.abs(c.volume - anchor.volume) <= volTol) push(sameVolume, c);
  }
  const diffLine = (c: FlowPatternSurveyCandidate) =>
    c.expiryYmd !== anchor.expiryYmd || c.strike !== anchor.strike || c.right !== anchor.right;
  const rankedOther = [...candidates].filter((c) => diffLine(c)).sort((a, b) => {
    const ae = a.expiryYmd !== anchor.expiryYmd ? 0 : 1;
    const be = b.expiryYmd !== anchor.expiryYmd ? 0 : 1;
    if (ae !== be) return ae - be;
    if (a.timeDeltaMs !== b.timeDeltaMs) return a.timeDeltaMs - b.timeDeltaMs;
    return Math.abs(a.strike - anchor.strike) - Math.abs(b.strike - anchor.strike);
  });
  for (const c of rankedOther) {
    push(otherLine, c);
  }
  return { sameClock, sameVolume, otherLine };
}

/**
 * Same-contract heuristics: opposite side + time order, optional OI snapshot delta, print size vs anchor.
 * Indicative only — not a definitive open/close classifier.
 */
function enrichPositionHints(anchor: NormalizedFlowLeg, leg: NormalizedFlowLeg, c: FlowPatternSurveyCandidate): void {
  if (!sameContractIdentity(anchor, leg)) {
    c.sameContractAsAnchor = false;
    return;
  }
  c.sameContractAsAnchor = true;
  const opp = executionSideMismatch(anchor, leg);
  const later = leg.timestampMs > anchor.timestampMs;
  const earlier = leg.timestampMs < anchor.timestampMs;
  if (opp && later) {
    c.oppositeSideHintKey = 'flowPattern.hintPossibleCloseLater';
  } else if (opp && earlier) {
    c.oppositeSideHintKey = 'flowPattern.hintOppositeSideEarlier';
  }
  if (opp && later) {
    if (leg.volume < anchor.volume && anchor.volume > 0) {
      c.volumeRelHintKey = 'flowPattern.hintSmallerPrintLater';
    } else if (leg.volume > anchor.volume) {
      c.volumeRelHintKey = 'flowPattern.hintLargerPrintLater';
    }
  }
  const oiA = anchor.openInterest;
  const oiL = leg.openInterest;
  if (oiA != null && oiL != null && Number.isFinite(oiA) && Number.isFinite(oiL)) {
    if (oiL < oiA) c.oiVsAnchorHintKey = 'flowPattern.hintOiLowerThanAnchor';
    else if (oiL > oiA) c.oiVsAnchorHintKey = 'flowPattern.hintOiHigherThanAnchor';
    else c.oiVsAnchorHintKey = 'flowPattern.hintOiSameAsAnchor';
  }
}

/** True when anchor and leg share the same printed time within epsilon (multi-leg / package hint). */
export function samePrintedClock(anchorMs: number, legMs: number, epsilonMs: number): boolean {
  const e = Math.max(0, epsilonMs);
  return Math.abs(legMs - anchorMs) <= e;
}

/**
 * Count distinct legs in `pool` (same underlying) whose clock matches `anchorMs` within epsilon, excluding `excludeIds`.
 */
export function countCoPrintPeersAtAnchorClock(
  anchorMs: number,
  pool: NormalizedFlowLeg[],
  underlying: string,
  epsilonMs: number,
  excludeIds: Set<string>,
): number {
  const u = underlying.toUpperCase();
  const e = Math.max(0, epsilonMs);
  let n = 0;
  for (const leg of pool) {
    if (leg.underlying.toUpperCase() !== u) continue;
    if (excludeIds.has(leg.legId)) continue;
    if (samePrintedClock(anchorMs, leg.timestampMs, e)) n += 1;
  }
  return n;
}

function sideAlignedForDisplay(anchor: NormalizedFlowLeg, leg: NormalizedFlowLeg): boolean | undefined {
  if (!hasExecutableSide(anchor.executionHint) || !hasExecutableSide(leg.executionHint)) return undefined;
  return anchor.executionHint === leg.executionHint;
}

function scoreCandidate(
  anchor: NormalizedFlowLeg,
  leg: NormalizedFlowLeg,
  opts: FlowPatternSurveyOptions,
): {
  ok: boolean;
  score: number;
  timeDeltaMs: number;
  volumeDelta: number;
  volumeSpreadPct?: number;
  premiumDeltaPct: number;
  sideAligned?: boolean;
} {
  if (leg.underlying.toUpperCase() !== anchor.underlying.toUpperCase()) {
    return { ok: false, score: 0, timeDeltaMs: 0, volumeDelta: 0, premiumDeltaPct: 100 };
  }
  const timeDeltaMs = Math.abs(leg.timestampMs - anchor.timestampMs);
  if (timeDeltaMs > opts.timeWindowMs) return { ok: false, score: 0, timeDeltaMs, volumeDelta: 0, premiumDeltaPct: 100 };

  if (opts.matchMode === 'multileg') {
    if (opts.multilegSameExpiryOnly && leg.expiryYmd !== anchor.expiryYmd) {
      return { ok: false, score: 0, timeDeltaMs, volumeDelta: 0, premiumDeltaPct: 100 };
    }
    const vmax = Math.max(Math.abs(leg.volume), Math.abs(anchor.volume), 1);
    const volumeSpreadPct = (Math.abs(leg.volume - anchor.volume) / vmax) * 100;
    const pmax = Math.max(Math.abs(leg.premium), Math.abs(anchor.premium), 1);
    const premiumSpreadPct = (Math.abs(leg.premium - anchor.premium) / pmax) * 100;
    const volTol = Math.max(opts.volumeTolerance, 0.01);
    const premTol = Math.max(opts.premiumTolerancePct, 0.01);
    const match = volumeSpreadPct <= volTol && premiumSpreadPct <= premTol;
    const sideMis = executionSideMismatch(anchor, leg);
    const sidePenalty = sideMis ? EXEC_SIDE_PENALTY : 0;
    const score =
      timeDeltaMs / Math.max(opts.timeWindowMs, 1) +
      volumeSpreadPct / volTol +
      premiumSpreadPct / premTol +
      sidePenalty;
    return {
      ok: match,
      score,
      timeDeltaMs,
      volumeDelta: Math.abs(leg.volume - anchor.volume),
      volumeSpreadPct,
      premiumDeltaPct: premiumSpreadPct,
      sideAligned: sideAlignedForDisplay(anchor, leg),
    };
  }

  const volumeDelta = Math.abs(leg.volume - anchor.volume);
  const premAvg = (Math.abs(leg.premium) + Math.abs(anchor.premium)) / 2 || 1;
  const premiumDeltaPct = (Math.abs(leg.premium - anchor.premium) / premAvg) * 100;
  let match = true;
  if (opts.matchMode === 'volume' || opts.matchMode === 'both') {
    if (!volumeClose(leg.volume, anchor.volume, opts.volumeTolerance)) match = false;
  }
  if (opts.matchMode === 'premium' || opts.matchMode === 'both') {
    if (!premiumClose(leg.premium, anchor.premium, opts.premiumTolerancePct)) match = false;
  }
  const sideMis = executionSideMismatch(anchor, leg);
  const sidePenalty = sideMis ? EXEC_SIDE_PENALTY : 0;
  const score =
    timeDeltaMs / Math.max(opts.timeWindowMs, 1) +
    volumeDelta / Math.max(opts.volumeTolerance, 1) +
    premiumDeltaPct / Math.max(opts.premiumTolerancePct, 0.01) +
    sidePenalty;
  return { ok: match, score, timeDeltaMs, volumeDelta, premiumDeltaPct, sideAligned: sideAlignedForDisplay(anchor, leg) };
}

/**
 * Survey in-memory legs for same underlying and time/size/premium similarity (Odoo-style ranked suggestions).
 * Use `matchMode: 'multileg'` for co-temporal package legs: relative volume and premium **spread %** vs
 * `volumeTolerance` / `premiumTolerancePct` (not absolute contracts / average premium).
 */
export function surveySameWindow(
  anchor: NormalizedFlowLeg,
  dataset: NormalizedFlowLeg[],
  opts: FlowPatternSurveyOptions,
): FlowPatternSurveyCandidate[] {
  const ex = opts.excludeLegIds ?? new Set<string>();
  const out: FlowPatternSurveyCandidate[] = [];
  for (const leg of dataset) {
    if (leg.legId === anchor.legId || ex.has(leg.legId)) continue;
    const r = scoreCandidate(anchor, leg, opts);
    if (!r.ok) continue;
    out.push({
      ...leg,
      score: r.score,
      timeDeltaMs: r.timeDeltaMs,
      volumeDelta: r.volumeDelta,
      volumeSpreadPct: r.volumeSpreadPct,
      premiumDeltaPct: r.premiumDeltaPct,
      sideAligned: r.sideAligned,
    });
  }
  out.sort((a, b) => a.score - b.score);
  return out;
}

/** Normalized time distance for ranking (0 = same clock, capped). */
function normTimeDist(timeDeltaMs: number, tw: number, cap = 5): number {
  return Math.min(timeDeltaMs / Math.max(tw, 1), cap);
}

/** Normalized size distance vs anchor using vol/prem spread % and user tolerances (0 = same size). */
function normSizeDist(
  volumeSpreadPct: number | undefined,
  premiumDeltaPct: number,
  volTol: number,
  premTol: number,
  cap = 5,
): number {
  const vt = Math.max(volTol, 0.01);
  const pt = Math.max(premTol, 0.01);
  const v = volumeSpreadPct ?? 0;
  const p = premiumDeltaPct ?? 0;
  const vn = Math.min(v / vt, cap);
  const pn = Math.min(p / pt, cap);
  return (vn + pn) / 2;
}

/**
 * Partner-leg solver: same underlying, time window, optional strict size/premium spread filter.
 * Ranks candidates by time proximity, similar trade size (volume & premium vs tolerances), or a weighted blend—
 * so verticals, calendars, and mixed strike/expiry packages can surface without fixing expiry/strike upfront.
 * @see FlowPatternSurveyOptions.partnerRankingMode
 */
export function surveyTimeCluster(
  anchor: NormalizedFlowLeg,
  dataset: NormalizedFlowLeg[],
  opts: FlowPatternSurveyOptions,
): FlowPatternSurveyCandidate[] {
  const ex = opts.excludeLegIds ?? new Set<string>();
  const maxR = Math.min(Math.max(opts.maxClusterResults ?? 60, 1), 250);
  const requireSpread = opts.requireSizePremiumMatch === true;
  const maxDteExplicit = opts.maxLegDteDays;
  const maxDte =
    maxDteExplicit !== undefined && maxDteExplicit !== null
      ? maxDteExplicit
      : requireSpread
        ? 365
        : 0;
  const tw = Math.max(opts.timeWindowMs, 1);
  const volTol = Math.max(opts.volumeTolerance, 0.01);
  const premTol = Math.max(opts.premiumTolerancePct, 0.01);
  const partnerMode = opts.partnerRankingMode ?? 'time';
  const balance = Math.min(1, Math.max(0, opts.partnerBalance ?? 0.5));
  const printEps =
    opts.samePrintTimeEpsilonMs !== undefined && opts.samePrintTimeEpsilonMs !== null
      ? Math.max(0, opts.samePrintTimeEpsilonMs)
      : 1;
  const out: FlowPatternSurveyCandidate[] = [];
  for (const leg of dataset) {
    if (leg.underlying.toUpperCase() !== anchor.underlying.toUpperCase()) continue;
    if (leg.legId === anchor.legId || ex.has(leg.legId)) continue;
    const timeDeltaMs = Math.abs(leg.timestampMs - anchor.timestampMs);
    if (timeDeltaMs > opts.timeWindowMs) continue;
    if (opts.multilegSameExpiryOnly && leg.expiryYmd !== anchor.expiryYmd) continue;
    if (maxDte > 0) {
      const dte = calendarDaysToExpiry(anchor.timestampMs, leg.expiryYmd);
      if (dte > maxDte || dte < -30) continue;
    }
    if (opts.requireSameExecutionSide && executionSideMismatch(anchor, leg)) continue;
    const r = scoreCandidate(anchor, leg, { ...opts, matchMode: 'multileg' });
    if (requireSpread && !r.ok) continue;
    const cand: FlowPatternSurveyCandidate = {
      ...leg,
      score: r.score,
      timeDeltaMs: r.timeDeltaMs,
      volumeDelta: r.volumeDelta,
      volumeSpreadPct: r.volumeSpreadPct,
      premiumDeltaPct: r.premiumDeltaPct,
      sideAligned: r.sideAligned,
      samePrintTimeAsAnchor: samePrintedClock(anchor.timestampMs, leg.timestampMs, printEps),
      strikeDeltaAbs: Math.abs(leg.strike - anchor.strike),
      expiryDeltaDays: expiryCalendarDeltaDays(anchor.expiryYmd, leg.expiryYmd),
    };
    enrichPositionHints(anchor, leg, cand);
    out.push(cand);
  }
  const secondaryOnly = (c: FlowPatternSurveyCandidate) => c.score - c.timeDeltaMs / tw;
  const sideSortRank = (c: FlowPatternSurveyCandidate) =>
    c.sideAligned === false ? 1 : c.sideAligned === true ? 0 : 0.5;
  const printSortRank = (c: FlowPatternSurveyCandidate) => (c.samePrintTimeAsAnchor ? 0 : 1);

  const tieBreakTimeFirst = (a: FlowPatternSurveyCandidate, b: FlowPatternSurveyCandidate): number => {
    const pr = printSortRank(a) - printSortRank(b);
    if (pr !== 0) return pr;
    const sr = sideSortRank(a) - sideSortRank(b);
    if (sr !== 0) return sr;
    if (a.expiryYmd !== b.expiryYmd) return a.expiryYmd.localeCompare(b.expiryYmd);
    if (a.strike !== b.strike) return a.strike - b.strike;
    return a.right.localeCompare(b.right);
  };

  for (const c of out) {
    const tN = normTimeDist(c.timeDeltaMs, tw);
    const sN = normSizeDist(c.volumeSpreadPct, c.premiumDeltaPct, volTol, premTol);
    if (requireSpread) {
      c.solverScore = c.score;
    } else if (partnerMode === 'time') {
      c.solverScore = tN;
    } else if (partnerMode === 'size') {
      c.solverScore = sN;
    } else {
      c.solverScore = (1 - balance) * tN + balance * sN;
    }
  }

  if (!requireSpread) {
    if (partnerMode === 'time') {
      out.sort((a, b) => {
        if (a.timeDeltaMs !== b.timeDeltaMs) return a.timeDeltaMs - b.timeDeltaMs;
        return tieBreakTimeFirst(a, b);
      });
    } else if (partnerMode === 'size') {
      out.sort((a, b) => {
        const sa = normSizeDist(a.volumeSpreadPct, a.premiumDeltaPct, volTol, premTol);
        const sb = normSizeDist(b.volumeSpreadPct, b.premiumDeltaPct, volTol, premTol);
        if (Math.abs(sa - sb) > 1e-9) return sa - sb;
        if (a.timeDeltaMs !== b.timeDeltaMs) return a.timeDeltaMs - b.timeDeltaMs;
        return tieBreakTimeFirst(a, b);
      });
    } else {
      out.sort((a, b) => {
        const ca =
          (1 - balance) * normTimeDist(a.timeDeltaMs, tw) +
          balance * normSizeDist(a.volumeSpreadPct, a.premiumDeltaPct, volTol, premTol);
        const cb =
          (1 - balance) * normTimeDist(b.timeDeltaMs, tw) +
          balance * normSizeDist(b.volumeSpreadPct, b.premiumDeltaPct, volTol, premTol);
        if (Math.abs(ca - cb) > 1e-9) return ca - cb;
        if (a.timeDeltaMs !== b.timeDeltaMs) return a.timeDeltaMs - b.timeDeltaMs;
        return tieBreakTimeFirst(a, b);
      });
    }
  } else {
    out.sort((a, b) => {
      if (a.timeDeltaMs !== b.timeDeltaMs) return a.timeDeltaMs - b.timeDeltaMs;
      const pr = printSortRank(a) - printSortRank(b);
      if (pr !== 0) return pr;
      return secondaryOnly(a) - secondaryOnly(b);
    });
  }
  return out.slice(0, maxR);
}

/** Alias for {@link surveyTimeCluster} (multi-expiry / multi-strike partner ranking). */
export const solvePartnerLegs = surveyTimeCluster;

function sameExpiry(a: NormalizedFlowLeg, b: NormalizedFlowLeg): boolean {
  return a.expiryYmd === b.expiryYmd;
}

/** Signed premium cash at print: buy = debit (−), sell = credit (+). Mid/unknown = 0. */
export function legSignedCashAtOpen(leg: NormalizedFlowLeg): number {
  const p = Math.abs(leg.premium);
  if (leg.executionHint === 'buy') return -p;
  if (leg.executionHint === 'sell') return p;
  return 0;
}

/**
 * Heuristic multi-leg label from a small set of legs (user-confirmed or suggested).
 */
export function classifyLinkedLegs(legs: NormalizedFlowLeg[]): FlowPatternClassification {
  const notes: string[] = [];
  if (!legs.length) {
    return { labelKey: 'flowPattern.none', confidence: 'low', directionKey: 'flowPattern.directionUnknown', notes };
  }
  if (legs.length === 1) {
    return {
      labelKey: 'flowPattern.singleLeg',
      confidence: 'high',
      directionKey: legs[0].right === 'C' ? 'flowPattern.callBias' : 'flowPattern.putBias',
      notes,
    };
  }
  const sorted = [...legs].sort((a, b) => a.strike - b.strike);
  const expiries = new Set(sorted.map((l) => l.expiryYmd));
  const rights = new Set(sorted.map((l) => l.right));
  const strikes = Array.from(new Set(sorted.map((l) => l.strike))).sort((a, b) => a - b);

  if (sorted.length === 2 && expiries.size === 2 && rights.size === 1 && strikes.length === 1) {
    return {
      labelKey: 'flowPattern.calendar',
      confidence: 'medium',
      directionKey: 'flowPattern.directionDeferred',
      notes: [...notes, 'flowPattern.noteTwoExpSameStrike'],
    };
  }

  if (sorted.length === 2 && expiries.size === 1 && rights.size === 1 && strikes.length === 2) {
    const r0 = sorted[0].right;
    return {
      labelKey: r0 === 'C' ? 'flowPattern.callVertical' : 'flowPattern.putVertical',
      confidence: 'medium',
      directionKey: 'flowPattern.directionDeferred',
      notes,
    };
  }

  if (sorted.length === 2 && expiries.size === 1 && strikes.length === 1 && rights.size === 2) {
    return {
      labelKey: 'flowPattern.straddle',
      confidence: 'medium',
      directionKey: 'flowPattern.directionVolatile',
      notes,
    };
  }

  if (sorted.length >= 3 && expiries.size === 1 && strikes.length >= 2) {
    return {
      labelKey: 'flowPattern.complexSameExpiry',
      confidence: 'low',
      directionKey: 'flowPattern.directionUnknown',
      notes: [...notes, 'flowPattern.noteReviewManually'],
    };
  }

  return {
    labelKey: 'flowPattern.unclassified',
    confidence: 'low',
    directionKey: 'flowPattern.directionUnknown',
    notes: [...notes, 'flowPattern.noteReviewManually'],
  };
}

export function estimatePayoffAtFills(legs: NormalizedFlowLeg[]): FlowPatternPayoffEstimate {
  if (!legs.length) {
    return { netCashAtOpen: 0, netLabelKey: 'flowPattern.netFlat', rangeUnknown: true };
  }
  const net = legs.reduce((s, l) => s + legSignedCashAtOpen(l), 0);
  const netLabelKey =
    net > 1e-6 ? 'flowPattern.netCredit' : net < -1e-6 ? 'flowPattern.netDebit' : 'flowPattern.netFlat';

  if (legs.length === 2 && legs[0].expiryYmd === legs[1].expiryYmd && legs[0].right === legs[1].right && legs[0].strike !== legs[1].strike) {
    const k = legs.map((l) => l.strike).sort((a, b) => a - b);
    const width = Math.abs(k[1] - k[0]) * 100;
    const debitLike = net < 0;
    return {
      netCashAtOpen: net,
      netLabelKey,
      maxLossAtEntry: debitLike ? Math.abs(net) : undefined,
      maxGainAtEntry: debitLike ? width - Math.abs(net) : undefined,
      rangeUnknown: !debitLike,
    };
  }

  return { netCashAtOpen: net, netLabelKey, rangeUnknown: true };
}

export interface PatternCandidatesRequestBody {
  ticker: string;
  center_ts_iso: string;
  window_ms: number;
  match_mode?: FlowPatternMatchMode;
  anchor_volume?: number;
  anchor_premium?: number;
  volume_tolerance?: number;
  premium_tolerance_pct?: number;
  strike_min?: number;
  strike_max?: number;
  /** When true, backend applies strike_min/max on `flow_buffer`. Omit/false = all strikes (default). */
  apply_strike_band?: boolean;
  /** When true, pattern window does not narrow by strike (best for floor / multi-strike packages). */
  is_floor_package?: boolean;
  expiry_mode?: 'same' | 'any';
  anchor_expiry?: string;
  limit?: number;
}

export async function fetchFlowPatternCandidates(body: PatternCandidatesRequestBody): Promise<FlowRowLike[]> {
  const url = getApiUrl('/api/options-flow/pattern-candidates');
  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  const token = getAuthToken();
  if (token) headers.Authorization = `Bearer ${token}`;
  const payload: PatternCandidatesRequestBody = { ...body };
  if (payload.match_mode === 'multileg') {
    payload.match_mode = 'both';
  }
  const res = await fetch(url, { method: 'POST', headers, body: JSON.stringify(payload) });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    throw new Error(typeof data?.error === 'string' ? data.error : 'Request failed');
  }
  if (Array.isArray(data?.data)) return data.data as FlowRowLike[];
  return [];
}

/** Merges local buffer with remote enriched ticks (NBBO); same request body as pattern-candidates. */
export async function fetchPatternCandidatesEnriched(body: PatternCandidatesRequestBody): Promise<FlowRowLike[]> {
  const url = getApiUrl('/api/options-flow/pattern-candidates-enriched');
  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  const token = getAuthToken();
  if (token) headers.Authorization = `Bearer ${token}`;
  const payload: PatternCandidatesRequestBody = { ...body };
  if (payload.match_mode === 'multileg') {
    payload.match_mode = 'both';
  }
  const res = await fetch(url, { method: 'POST', headers, body: JSON.stringify(payload) });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    throw new Error(typeof data?.error === 'string' ? data.error : 'Request failed');
  }
  if (Array.isArray(data?.data)) return data.data as FlowRowLike[];
  return [];
}

/** Normalize many flow rows; skips invalid. */
export function normalizeFlowRowDataset(rows: FlowRowLike[]): NormalizedFlowLeg[] {
  const out: NormalizedFlowLeg[] = [];
  rows.forEach((row, i) => {
    const n = normalizeFlowRow(row, i);
    if (n) out.push(n);
  });
  return out;
}
